import { useManager } from 'dreamstate';
import { useRef, useState, useCallback, ChangeEvent, FormEvent, useEffect } from 'react';

import {
  Box,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  rem,
} from '@vimeo/bokeh';
import { Send, CloseX } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';

import { browserConfig } from '../../../../configs/BrowserConfig';
import { interactionToolsConfig } from '../../../../configs/InteractionToolsConfig';
import {
  TNotificationHandler,
  useSimpleNotification,
} from '../../../../hooks/live/useActionConfirmation';
import { IQnAContext, QnAManager } from '../../../../store/firebase/interaction/qna';
import {
  trackReplyAndApproveQuestion,
  trackReplyQuestion,
} from '../../../../tracking/tracking_engagement_qna';
import {
  T_REPLY,
  T_REPLY_AND_APPROVE,
  T_REPLY_POSTED,
  T_REPLY_POSTED_AND_APPROVED,
} from '../../../../translations';
import { stopEventPropagation } from '../../../../utils/dom/event';
import { createDomName } from '../../../../utils/dom/identifying';
import { FAILED_STATUS_COLOR } from '../../../../utils/styling/colors';
import { BokehTooltip } from '../../../bokeh';
import { ISelectedEmoji } from '../../../emoji';
import { EmojiButton } from '../../shared/EmojiButton';

interface IQnaReplyFormProps extends IBaseDomElementProps {
  selectedQuestionId: string;
  isApproveNeeded: boolean;
  qnaContext?: IQnAContext;
  onDismiss: () => void;
}

/**
 * Form to show when have selected question and can reply to it.
 * Used by moderators to add new messages as response to provided questions.
 */
export function QnaReplyForm({
  id = createDomName('qna-reply-form'),
  className = createDomName('qna-reply-form'),
  selectedQuestionId,
  isApproveNeeded,
  onDismiss,
  // eslint-disable-next-line react-hooks/rules-of-hooks
  qnaContext: { qnaActions } = useManager(QnAManager, () => []),
}: IQnaReplyFormProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [reply, setReply] = useState<string>('');
  const [submitTimeout, setSubmitTimeout] = useState<boolean>(false);

  const canSubmit: boolean = Boolean(
    reply.length &&
    reply.length <= interactionToolsConfig.QNA.MAX_QUESTION_REPLY_LENGTH &&
    !submitTimeout,
  );
  const charsLeft: number = interactionToolsConfig.QNA.MAX_QUESTION_REPLY_LENGTH - reply.length;
  const isCharsNearLimit: boolean = charsLeft <= 20;
  const isCharsOverLimit: boolean = charsLeft < 0;

  const notify: TNotificationHandler = useSimpleNotification();

  const onSubmitQuestionReply = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!reply.length || reply.length > interactionToolsConfig.QNA.MAX_QUESTION_REPLY_LENGTH) {
        return;
      }

      if (submitTimeout) {
        return;
      } else {
        setSubmitTimeout(true);
      }

      try {
        await (isApproveNeeded
          ? qnaActions.approveQuestion(selectedQuestionId)
          : Promise.resolve());

        await qnaActions.submitQuestionReply(selectedQuestionId, reply);

        setReply('');

        if (isApproveNeeded) {
          trackReplyAndApproveQuestion();
        } else {
          trackReplyQuestion();
        }
        notify({
          message: isApproveNeeded ? T_REPLY_POSTED_AND_APPROVED : T_REPLY_POSTED,
        });

        setTimeout(
          () => setSubmitTimeout(false),
          interactionToolsConfig.QNA.QUESTION_REPLY_THROTTLE,
        );
      } catch (error) {
        setSubmitTimeout(false);
      }
    },
    [qnaActions, isApproveNeeded, notify, reply, selectedQuestionId, submitTimeout],
  );

  const onEmojiSelect = useCallback((selection: ISelectedEmoji) => {
    if (inputRef.current) {
      const cursorPosition = inputRef.current.selectionStart || 0;
      const value = inputRef.current.value;
      const newValue =
        value.slice(0, cursorPosition) +
        selection.native +
        value.slice(cursorPosition, value.length);
      setReply(newValue);
      inputRef.current.focus();
    }
  }, []);

  const onQuestionEdited = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setReply(event.target.value);
  }, []);

  useEffect(() => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedQuestionId]);

  return (
    <Box
      id={id}
      className={className}
      as={'form'}
      display={'flex'}
      flexDirection={'column'}
      width={'100%'}
      paddingTop={rem(8)}
      onSubmit={onSubmitQuestionReply}
      onKeyUp={stopEventPropagation}
      onKeyDown={stopEventPropagation}
      onKeyPress={stopEventPropagation}
    >
      <Flex
        id={createDomName(id, 'title')}
        className={createDomName(className, 'title')}
        justifyContent={'space-between'}
        alignItems={'center'}
        marginBottom={rem(12)}
        fontSize={'text-xs'}
        fontWeight={'bold'}
      >
        <div>{isApproveNeeded ? T_REPLY_AND_APPROVE : T_REPLY}</div>

        <IconButton
          id={createDomName(id, 'dismiss')}
          className={createDomName(className, 'dismiss')}
          aria-label={'stop-reply'}
          size={'xs'}
          variant={'tertiary'}
          type={'button'}
          icon={<CloseX />}
          onClick={onDismiss}
        />
      </Flex>

      <InputGroup
        id={createDomName(id, 'controls')}
        className={createDomName(className, 'controls')}
      >
        {browserConfig.BROWSER?.isMobile ? null : (
          <InputLeftElement>
            <EmojiButton
              id={createDomName(id, 'emoji-button')}
              className={createDomName(className, 'emoji-button')}
              inputRef={inputRef}
              isSubmitted={submitTimeout}
              placement={'top-start'}
              onEmojiSelect={onEmojiSelect}
            />
          </InputLeftElement>
        )}

        <Input
          id={createDomName(id, 'input')}
          className={createDomName(className, 'input')}
          ref={inputRef}
          maxLength={interactionToolsConfig.CHAT.MAX_MESSAGE_LENGTH}
          value={reply}
          autoComplete={'off'}
          onChange={onQuestionEdited}
        />

        <InputRightElement>
          <IconButton
            id={createDomName(id, 'send-button')}
            className={createDomName(className, 'send-button')}
            aria-label={'qna-submit-button'}
            data-chat-submit
            type={'submit'}
            size={'sm'}
            variant={'tertiary'}
            icon={<Send />}
            transition={'none'}
            _hover={{
              backgroundColor: 'transparent!important',
            }}
            isDisabled={!canSubmit}
          />
        </InputRightElement>
      </InputGroup>

      <Flex
        id={createDomName(id, 'info')}
        className={createDomName(className, 'info')}
        justifyContent={'flex-end'}
        alignContent={'center'}
        width={'100%'}
      >
        <Flex
          id={createDomName(id, 'characters-count')}
          className={createDomName(className, 'characters-count')}
          as={'span'}
          alignItems={'center'}
          fontSize={'text-xs'}
          lineHeight={'text-xs'}
          padding={`${rem(8)} 0 0`}
          fontWeight={isCharsNearLimit || isCharsOverLimit ? 'bold' : 400}
          color={isCharsOverLimit ? FAILED_STATUS_COLOR : 'text-secondary'}
        >
          {`${reply.length}/${interactionToolsConfig.QNA.MAX_QUESTION_REPLY_LENGTH}`}
        </Flex>
      </Flex>
    </Box>
  );
}
